// Angular imports
import { Component, OnInit, OnDestroy, Input, HostListener } from '@angular/core';

// Import rxjs
import { Observable, Subject, combineLatest, takeUntil } from 'rxjs';

// Import modals and modal service
import { ModalService } from '../../modal/modal.service';
import { AddStabuRowModalComponent } from '../../modals/add-stabu-row-modal/add-stabu-row-modal.component';
import { AddLibraryItemModalComponent } from '../../modals/add-library-item-modal/add-library-item-modal.component';
import { ImportLinesModalComponent } from '../../modals/import-lines-modal/import-lines-modal.component';

// Import types
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state-management/app-state';
import * as CalculationActions from 'src/app/state-management/calculation-actions';
import { selectCalculationMeta, selectCurrentSelection, selectIsLatestVersion, selectLines, selectTail } from 'src/app/state-management/calculation-selectors';
import { CalculationLineApi, CalculationMetaApi, CalculationTailApi } from 'src/app/types';
import { TailLineType, ViewModelLine, isCalculationLineApi, isCalculationTailApi } from 'src/app/state-management/calculation-state';
import { LINE_CODE_SORT_FNC, LINE_SORTORDER_FNC } from 'src/app/state-management/functions/convert-calculation-line';
import { CreateLineService } from 'src/app/services/create-line/create-line.service';

@Component({
    selector: 'main-menu-bar-options',
    templateUrl: './main-menu-bar-options.component.html',
    styleUrls: ['./main-menu-bar-options.component.scss'],
    standalone: false
})
export class MainMenuBarOptionsComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    @Input() public canInsert: boolean = false;
    @Input() public hasSelection: boolean = false;
    @Input() public isTableLocked: boolean = false;

    // Will be true if the selected line has this property set
    // Used to display sub total line after the selection
    public isSectionEnd: boolean = false;
    public singleSelected: boolean = false;
    public multipleSelected: boolean = false;
    public selectedLines: (CalculationLineApi | CalculationTailApi)[] = [];
    public selectedTailLines: CalculationTailApi[] = [];
    public calculationLineSelected: boolean = false;
    public meta: CalculationMetaApi = undefined;
    public lines: CalculationLineApi[] = [];
    public tailLines: CalculationTailApi[] = [];

    public show: 'calculation' | 'all' | 'tail' = 'all';

    // State management solution
    private _isLatestVersion$: Observable<boolean>;

    constructor(
        private modalService: ModalService,
        private store: Store<AppState>,
        private createLineService: CreateLineService,
    ) {
    }

    public ngOnInit(): void {
        // Set whether the table is locked
        this._isLatestVersion$ = this.store.select(selectIsLatestVersion).pipe(
            takeUntil(this.unsubscribe$)
        );

        this._isLatestVersion$.subscribe(latestVersion => {
            this.isTableLocked = !latestVersion;
        })

        // Check if there is a current selection
        combineLatest([
            this.store.select(selectCurrentSelection),
            this.store.select(selectLines),
            this.store.select(selectTail)
        ])
            .pipe(
                takeUntil(this.unsubscribe$)
            ).subscribe(([selection, lines, tail]) => {
                // console.debug('[MainMenuBarOptionsComponent] selection', selection, lines, tail);
                this.lines = [...lines].sort(LINE_CODE_SORT_FNC);
                this.tailLines = [...tail].sort(LINE_SORTORDER_FNC);
                this.selectedLines = [...selection?.selectedLines ?? []];
                this.singleSelected = selection?.selectedLines?.length === 1;
                this.multipleSelected = selection?.selectedLines?.length > 0;
                this.calculationLineSelected = !!selection?.selectedLines.some(line => isCalculationLineApi(line));

                // console.debug('[main-menu-bar-options] selectedLines', this.selectedLines, this.singleSelected, this.multipleSelected, this.calculationLineSelected);

                const firstSelected = selection?.selectedLines[0];
                if (this.singleSelected) {
                    this.isSectionEnd = this.singleSelected && firstSelected.endOfSection;
                }
            })

        // Check if there is a current selection
        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(meta => {
            this.meta = meta;
        })
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    /* --- Event handlers --- */

    public changeVisibleParts(show: 'calculation' | 'all' | 'tail') {
        // console.log('changeVisibleParts', show);	
        this.store.dispatch(CalculationActions.updateVisibleParts({ show }));
    }

    public async _insertLine(event: MouseEvent, isTextLine: boolean = false) {
        if (!this.singleSelected) return;

        const firstSelected = this.selectedLines[0];

        if (isCalculationLineApi(firstSelected)) {
            await this.createLineService.createCalculationLine(isTextLine, !event.shiftKey);
            // const newLine = createLine(this.lines, firstSelected.line, isTextLine, !event.shiftKey);

            // this.store.dispatch(CalculationActions.insertLine({
            //     projectId: this.meta.projectId,
            //     calculationId: this.meta.id,
            //     line: newLine
            // }))
        } else if (isCalculationTailApi(firstSelected)) {
        }
    }

    public async _insertTailLine(event: MouseEvent) {
        // For tail line insert, no selection is needed
        await this.createLineService.createTailLine(!event.shiftKey);
        
        // const newTailLine = createTailLine(
        //     this.tailLines, this.selectedLines.find(f => isCalculationTailApi(f.line))?.line, !event.shiftKey);

        // this.store.dispatch(CalculationActions.insertTailLine({
        //     projectId: this.meta.projectId,
        //     calculationId: this.meta.id,
        //     line: newTailLine
        // }))
    }

    public _deleteSelection() {
        if (this.isTableLocked || !this.selectedLines.length || !this.meta) {
            return;
        }

        // Dispatch for every line to delete
        this.selectedLines.forEach(selectedLine => {
            if (isCalculationLineApi(selectedLine)) {
                this.store.dispatch(CalculationActions.deleteLine({
                    projectId: this.meta.projectId,
                    calculationId: this.meta.id,
                    line: selectedLine
                }))
            } else if (isCalculationTailApi(selectedLine)) {
                this.store.dispatch(CalculationActions.deleteTailLine({
                    projectId: this.meta.projectId,
                    calculationId: this.meta.id,
                    line: selectedLine
                }))

                // Removed the SUBTOTAL tail line and used the endOfSection property that is also part of calculation lines
                // // Also delete subtotal lines directly below on of the lines that is deleted
                // const selectedIndex = this.tailLines.findIndex(l => l.id === viewModelLine.line.id);
                // const isSectionEnd = selectedIndex >= 0 && selectedIndex < this.tailLines.length - 1 && this.tailLines[selectedIndex + 1].type === 'SUBTOTAL';

                // if (isSectionEnd) {
                //     // Delete the subtotal line
                //     this.store.dispatch(CalculationActions.deleteTailLine({
                //         projectId: this.meta.projectId,
                //         calculationId: this.meta.id,
                //         line: this.tailLines[selectedIndex + 1],
                //     }))
                // }
            }
        })
    }

    @HostListener('window:keydown', ['$event'])
    public _listenToCtrlCombi(event?: KeyboardEvent) {
        if (!event.altKey) {
            return;
        }

        switch (event.key) {
            case '1':
                break;
            case '2':
                break;
            case '3':
                break;
            case '5':
                // Toggle line type
                this._toggleLineType();
                break;
            default: return;
        }
    }

    @HostListener('window:keydown.f4', ['$event'])
    public _changeLineType(event?: KeyboardEvent) {
        this._toggleLineType(event);
    }

    @HostListener('window:keydown.f7', ['$event'])
    public _showAddItemModal(event?: KeyboardEvent) {
        if (this.isTableLocked || !this.meta?.projectId || !this.meta?.id) return;

        if (event) {
            // Stop further propagation
            event.stopPropagation();
            event.preventDefault();
        }

        // Open the modal
        this.modalService.open(AddStabuRowModalComponent, {
            addRows: async (lines: CalculationLineApi[]) => await this.createLineService.createAndInsertCalculationLines(lines, true, 'create')
        });
    }

    @HostListener('window:keydown.f6', ['$event'])
    public _showAddLibraryItemModal(event?: KeyboardEvent) {
        if (this.isTableLocked) return;

        if (event) {
            // Stop further propagation
            event.stopPropagation();
            event.preventDefault();
        }

        // Open the modal
        this.modalService.open(AddLibraryItemModalComponent, {
            addRows: (lines: CalculationLineApi[]) => 
                this.createLineService.createAndInsertCalculationLines(lines, true, 'create')
        });
    }

    public _showCopyLinesModal() {
        if (this.isTableLocked) return;

        // Open the modal
        this.modalService.open(ImportLinesModalComponent, {});
    }

    public _toggleSectionEnd(event?: KeyboardEvent) {
        console.debug('[main-menu-bar-options] _toggleSectionEnd', this.isTableLocked, this.singleSelected, this.meta);
        if (this.isTableLocked || !this.singleSelected || !this.meta) {
            return;
        }

        if (event) {
            // Stop further propagation
            event.stopPropagation();
            event.preventDefault();
        }

        const firstSelected = JSON.parse(JSON.stringify(this.selectedLines[0])) as CalculationLineApi | CalculationTailApi;

        console.debug('[main-menu-bar-options] _toggleSectionEnd', firstSelected, this.selectedLines[0]);

        if (isCalculationLineApi(firstSelected)) {
            // Toggle section end
            firstSelected.endOfSection = !firstSelected.endOfSection;

            // Update line
            this.store.dispatch(CalculationActions.updateLineUi({
                projectId: this.meta.projectId,
                calculationId: this.meta.id,
                line: firstSelected
            }));
        } else if (isCalculationTailApi(firstSelected)) {
            // Toggle section end
            firstSelected.endOfSection = !firstSelected.endOfSection;

            // Update line
            this.store.dispatch(CalculationActions.updateTailLineUi({
                projectId: this.meta.projectId,
                calculationId: this.meta.id,
                line: firstSelected
            }));
        }
    }

    public _toggleLineType(event?: KeyboardEvent) {
        console.debug('[MainMenuBarOptionsComponent] _toggleLineType', event);
        if (this.isTableLocked || !this.singleSelected || !this.meta) {
            return;
        }

        if (event) {
            // Stop further propagation
            event.stopPropagation();
            event.preventDefault();
        }

        const firstSelected = JSON.parse(JSON.stringify(this.selectedLines[0])) as CalculationLineApi | CalculationTailApi;

        if (isCalculationLineApi(firstSelected)) {
            // Toggle section end
            const line: CalculationLineApi = {
                ...firstSelected,
                description: this.selectedLines[0].description?.replace(/<.*?>/g, ' ') ?? '',
                hours: {},
                equipment: {},
                material: {},
                subcontracting: {},
                unit: firstSelected.isTextLine ? firstSelected.unit || 'stk' : firstSelected.unit,
                isTextLine: !firstSelected.isTextLine
            };

            // Update line
            this.store.dispatch(CalculationActions.updateLineUi({
                projectId: this.meta.projectId,
                calculationId: this.meta.id,
                line: line
            }));
        } else if (isCalculationTailApi(firstSelected)) {
            let newType: string;

            switch ((firstSelected as CalculationTailApi).type) {
                case 'AMOUNT':
                    newType = 'PERCENTAGE';
                    break;
                case 'PERCENTAGE':
                    newType = 'TEXT'
                    break;
                case 'TEXT':
                    newType = 'AMOUNT'
                    break;
            }
            // Toggle section end
            const line: CalculationTailApi = {
                ...firstSelected,
                description: this.selectedLines[0].description?.replace(/<.*?>/g, ' ') ?? '',
                type: newType
            };

            // Update line
            this.store.dispatch(CalculationActions.updateTailLineUi({
                projectId: this.meta.projectId,
                calculationId: this.meta.id,
                line: line
            }));
        }
    }
}

